import React, { useState, useEffect } from "react";
import { useAuth } from "../../../hooks/useAuth";
import { adminAPI } from "../../../services/admin";
import LoadingSpinner from "../../common/LoadingSpinner/LoadingSpinner";
import FormEditor from "../../pages/FormEditor/FormEditor";
import AdvancedCategoriesPanel from "../../pages/AdvancedCategoriesPanel/AdvancedCategoriesPanel";
import StatusManagementPanel from "../../pages/StatusManagementPanel/StatusManagementPanel";
import {
  Clock,
  Folder,
  Clipboard,
  NotepadText,
  Hourglass,
  User,
  Users,
  X,
  File,
  UserRoundCog,
  Banknote,
  Check,
  Crown,
  ChartNoAxesColumn,
  Link,
  RefreshCcw,
  Plus,
  IdCard,
  Search,
  TriangleAlert,
  SquareCheckBig,
  MessageCircle,
  Edit,
  Settings,
  Trash,
  Lightbulb,
  Target,
  RotateCcwIcon,
} from "lucide-react";
import { useNotifications } from "@/hooks/useNotifications";
import ChatAdminPanel from "../ChatAdminPanel/ChatAdminPanel";
import UserAdminPanel from "../UserAdminPanel/UserAdminPanel";
import { useParams, useNavigate } from "react-router-dom";
import FormConnectionModal from "@/components/common/FormConnectionModal/FormConnectionModal";
import CategoryChangeModal from "@/components/common/CategoryChangeModal/CategoryChangeModal";
import StatCard2 from "@/components/common/StatCard2/StatCard2";
import TramiteModal from "@/components/common/TramiteModal/TramiteModal";

const AdminPanel = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { success, error } = useNotifications();

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const { tab } = useParams();
  const navigate = useNavigate();

  const activeTab = tab || "dashboard";

  const changeTab = (newTab) => {
    navigate(`/admin/${newTab}`);
  };

  const fetchDashboardStats = async () => {
    try {
      const statsData = await adminAPI.getDashboardStats();
      setStats(statsData);
    } catch (error) {
      console.error("Error fetching admin stats:", error);
    } finally {
      setLoading(false);
    }
  };

  // Verificar permisos de admin
  if (user?.role !== "admin" && user?.role !== 'superadmin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Acceso Denegado
          </h2>
          <p className="text-gray-600">
            Solo los administradores pueden acceder a este panel.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <LoadingSpinner message="Cargando panel de administración..." />;
  }

  const tabs = [
    { id: "dashboard", name: "Dashboard", icon: <ChartNoAxesColumn /> },

    ...(user.role === "superadmin"
      ? [{ id: "categories", name: "Categorías", icon: <Folder /> }]
      : []),

    { id: "tramites", name: "Trámites", icon: <Clipboard /> },
    { id: "forms", name: "Formularios", icon: <Clipboard /> },
    { id: "gestores", name: "Gestores", icon: <UserRoundCog /> },
    { id: "estados", name: "Estados", icon: <RefreshCcw /> },
    { id: "documents", name: "Documentos", icon: <NotepadText /> },

    ...(user.role === "superadmin"
      ? [{ id: "users", name: "Usuarios", icon: <Users /> }]
      : []),

    { id: "support", name: "Soporte", icon: <MessageCircle /> },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-3">
          <div className="flex items-center justify-between h-16">
            <h1 className="text-2xl font-bold text-gray-900 flex row items-center">
              <Crown className="mr-2" /> Panel de Administración
            </h1>
            <div className="text-sm text-gray-500">
              Bienvenido, {user?.name}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation Tabs */}
        <div className="mb-8">
          <nav className="flex space-x-1 bg-gray-100 rounded-lg p-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => changeTab(tab.id)}
                className={`flex-1 flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeTab === tab.id
                    ? "bg-white text-blue-600 shadow"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-2xl shadow-lg">
          {activeTab === "dashboard" && <DashboardTab stats={stats} />}
          {activeTab === "categories" && <AdvancedCategoriesPanel />}
          {activeTab === "tramites" && <TramitesTab />}
          {activeTab === "gestores" && <GestoresTab />}
          {activeTab === "estados" && <StatusManagementPanel />}
          {activeTab === "forms" && <FormEditor />}
          {activeTab === "documents" && <DocumentsTab />}
          {activeTab === "users" && user.role === "superadmin" && <UsersTab />}
          {activeTab === "support" && <SupportTab />}
        </div>
      </div>
    </div>
  );
};

const DashboardTab = ({ stats }) => (
  <div className="p-8">
    <h2 className="text-2xl font-bold text-gray-900 mb-6">Dashboard</h2>

    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
      <StatCard2
        title="Categorías"
        value={stats?.total_categories || 0}
        icon={<Folder />}
        color="blue"
      />
      <StatCard2
        title="Trámites"
        value={stats?.total_tramites || 0}
        icon={<Clipboard />}
        color="green"
      />
      <StatCard2
        title="Solicitudes"
        value={stats?.total_requests || 0}
        icon={<NotepadText />}
        color="yellow"
      />
      <StatCard2
        title="Doc. Pendientes"
        value={stats?.pending_documents || 0}
        icon={<Hourglass />}
        color="red"
      />
    </div>

    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
      <StatCard2
        title="Clientes"
        value={stats?.active_clients || 0}
        icon={<User />}
        color="purple"
      />
      <StatCard2
        title="Gestores"
        value={stats?.total_gestores || 0}
        icon={<UserRoundCog />}
        color="indigo"
      />
      <StatCard2
        title="Cotizaciones"
        value={stats?.pending_quotations || 0}
        icon={<Banknote />}
        color="orange"
      />
      <StatCard2
        title="Completados"
        value={stats?.completed_tramites || 0}
        icon={<Check />}
        color="emerald"
      />
    </div>

    {/* Recent Activity */}
    <div className="bg-gray-50 rounded-lg p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Actividad Reciente
      </h3>
      <div className="space-y-3">
        {stats?.recent_requests?.map((request) => (
          <div
            key={request._id}
            className="flex items-center justify-between bg-white p-3 rounded-lg"
          >
            <div className="flex items-center space-x-3">
              <div className="text-xl">
                <Clipboard />
              </div>
              <div>
                <p className="font-medium text-gray-900">
                  {request.tramite_name}
                </p>
                <p className="text-sm text-gray-500">
                  Cliente: {request.client_name}
                </p>
              </div>
            </div>
            <div className="text-right">
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  request.status === "completado"
                    ? "bg-green-100 text-green-800"
                    : request.status === "en_proceso"
                      ? "bg-yellow-100 text-yellow-800"
                      : "bg-gray-100 text-gray-800"
                }`}
              >
                {request.status}
              </span>
              <p className="text-xs text-gray-500 mt-1">
                {new Date(request.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const TramitesTab = ({ onConfigureForm }) => {
  const [tramites, setTramites] = useState([]);
  const [categories, setCategories] = useState([]);
  const [states, setStates] = useState([]);
  const [formularios, setFormularios] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedStates, setSelectedStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTramite, setEditingTramite] = useState(null);
  const [showTramiteModal, setShowTramiteModal] = useState(false);
  const [showCategoryChangeModal, setShowCategoryChangeModal] = useState(false);
  const [showFormConnectionModal, setShowFormConnectionModal] = useState(false);
  const [selectedTramiteForMove, setSelectedTramiteForMove] = useState(null);
  const [tramiteParaConectar, setTramiteParaConectar] = useState(null);
  const [draggedTramite, setDraggedTramite] = useState(null);
  const { success, error } = useNotifications();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tramitesData, statesData, categoriesData, formulariosData] = await Promise.all(
        [adminAPI.getTramites(), adminAPI.getStates(), adminAPI.getCategories(), fetchFormularios()],
      );
      setTramites(tramitesData);
      setCategories(categoriesData);
      setStates(statesData);
      setFormularios(formulariosData);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFormularios = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/admin/form`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (response.ok) {
        return await response.json();
      }
      return [];
    } catch (error) {
      console.error("Error fetching formularios:", error);
      return [];
    }
  };

  const filteredTramites = tramites.filter((t) => {
    const matchCategory =
      selectedCategory === "all" ||
      (t.category_id?._id || t.category_id) === selectedCategory;

    const matchState =
      selectedStates.length === 0 ||
      !t.states?.length ||
      t.states.some((s) =>
        selectedStates.includes(s)
      );

    return matchCategory && matchState;
  });

  const handleCreateMissingForms = async () => {
    if (
      !window.confirm(
        "¿Deseas crear formularios automáticos para todos los trámites que no tienen formulario? Esto creará 97 formularios nuevos.",
      )
    ) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/admin/type/create-missing-forms`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.ok) {
        const result = await response.json();
        success(
          <SquareCheckBig /> +
            `${result.message}\n\nDetalles:\n• Formularios creados: ${result.formularios_creados}\n• Trámites procesados: ${result.total_tramites}`,
        );
        fetchData(); // Refrescar datos
      } else {
        const error = await response.json();
        success(`❌ Error: ${error.detail}`);
      }
    } catch (error) {
      console.error("Error creando formularios:", error);
      success("❌ Error de conexión al crear formularios");
    }
  };

  const handleConnectFormManual = async (formTemplateId) => {
    if (!tramiteParaConectar || !formTemplateId) return;

    try {
      const token = localStorage.getItem("token");

      const formularioSeleccionado = formularios.find(
        (f) => f._id === formTemplateId,
      );
      if (!formularioSeleccionado) {
        success("❌ Formulario seleccionado no encontrado");
        return;
      }

      console.log("🔗 Conectando por nombres (método robusto):", {
        tramite: tramiteParaConectar.name,
        formulario: formularioSeleccionado.name,
      });

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/admin/form/connect`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            tramite_name: tramiteParaConectar.name,
            form_name: formularioSeleccionado.name,
          }),
        },
      );

      if (response.ok) {
        const result = await response.json();
        success(
          `🎉 ¡CONEXIÓN EXITOSA!\n\n${result.message}\n\nDetalles:\n• Trámite: ${result.tramite_name}\n• Formulario: ${result.form_name}\n• Form ID: ${result.form_id}\n• Método: ${result.connection_method}`,
        );

        // Forzar refresh completo
        await fetchData();

        // Cerrar modal después del refresh
        setTimeout(() => {
          setShowFormConnectionModal(false);
          setTramiteParaConectar(null);
        }, 1000);
      } else {
        const error = await response.json();
        console.error("❌ Error de conexión robusto:", error);
        success(`❌ Error: ${error.detail}`);
      }
    } catch (error) {
      console.error("Error conectando formulario por nombres:", error);
      success("❌ Error de conexión de red");
    }
  };

  const handleAutoConnectForms = async () => {
    if (
      !window.confirm(
        '¿Auto-conectar formularios con nombres idénticos?\n\nEsto conectará trámites como "Avalúos" con formularios "Formulario Auto: Avalúos" automáticamente.',
      )
    ) {
      return;
    }

    try {
      const token = localStorage.getItem("token");

      let conexiones = 0;
      const detalleConexiones = [];

      // Conectar solo formularios con nombres que coinciden exactamente
      for (const tramite of tramites) {
        if (tramite.form_template_id) continue;

        const tramiteNombre = tramite.name;

        // Buscar formulario que coincida exactamente
        const formularioCoincidente = formularios.find(
          (form) =>
            form.name === `Formulario Auto: ${tramiteNombre}` ||
            form.name === tramiteNombre ||
            form.name.replace("Formulario Auto: ", "") === tramiteNombre,
        );

        if (formularioCoincidente) {
          // Conectar este formulario con body JSON
          const response = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/admin/form/${tramite._id}/auto-connect`,
            {
              method: "PUT",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                form_template_id: formularioCoincidente._id,
              }),
            },
          );

          if (response.ok) {
            conexiones++;
            detalleConexiones.push({
              tramite: tramiteNombre,
              formulario: formularioCoincidente.name,
              formId: formularioCoincidente._id.substring(0, 7),
            });
          }
        }
      }

      if (conexiones > 0) {
        success(
          <SquareCheckBig /> +
            ` Auto-conexión completada!\n\n${conexiones} formularios conectados por nombre idéntico.\n\nEjemplos:\n${detalleConexiones
              .slice(0, 3)
              .map((c) => `• ${c.tramite} → ${c.formId}`)
              .join("\n")}`,
        );
        fetchData(); // Refrescar datos
      } else {
        success(
          "ℹ️ No se encontraron nombres idénticos para auto-conectar.\n\nUsa la conexión manual para cada trámite específico.",
        );
      }
    } catch (error) {
      console.error("Error auto-conectando:", error);
      success("❌ Error de conexión");
    }
  };

  const handleDeleteTramite = async (tramiteId) => {
    if (window.confirm("¿Estás seguro de que quieres eliminar este trámite?")) {
      try {
        await adminAPI.deleteTramite(tramiteId);
        fetchData();
        success("Trámite eliminado exitosamente");
      } catch (error) {
        success("Error al eliminar trámite: " + error.message);
      }
    }
  };

  const handleChangeTramiteCategory = async (updateData) => {
    console.log(updateData);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/admin/type`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updateData),
        },
      );

      if (response.ok) {
        const result = await response.json();
        success(result.message);
        fetchData();
        setShowCategoryChangeModal(false);
        setSelectedTramiteForMove(null);
      } else {
        const error = await response.json();
        success(`Error: ${error.detail}`);
      }
    } catch (error) {
      success("Error cambiando categoría");
    }
  };

  const handleUpdateTramite = async (updateData) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/admin/type`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updateData),
        },
      );

      if (response.ok) {
        success("Trámite actualizado exitosamente");
        fetchData();
        setEditingTramite(null);
      } else {
        const error = await response.json();
        success(`Error: ${error.detail}`);
      }
    } catch (error) {
      success("Error actualizando trámite");
    }
  };

  const handleCreateTramite = async (tramiteData) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/admin/type`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(tramiteData),
        },
      );

      if (response.ok) {
        success("Trámite creado exitosamente");
        fetchData();
        setShowTramiteModal(false);
      } else {
        const error = await response.json();
        success(`Error: ${error.detail}`);
      }
    } catch (error) {
      success("Error creando trámite");
    }
  };

  const handleTramiteDragStart = (e, tramite) => {
    if (selectedCategory === "all") {
      e.preventDefault();
      success("Selecciona una categoría específica para reordenar trámites");
      return;
    }

    setDraggedTramite(tramite);
    e.dataTransfer.effectAllowed = "move";
    e.target.style.opacity = "0.5";
  };

  const handleTramiteDragEnd = (e) => {
    e.target.style.opacity = "1";
    setDraggedTramite(null);
  };

  const handleTramiteDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleTramiteDrop = (e, targetTramite) => {
    e.preventDefault();

    if (!draggedTramite || draggedTramite._id === targetTramite._id) return;

    if (draggedTramite.category_id !== targetTramite.category_id) {
      success("Solo puedes reordenar trámites dentro de la misma categoría");
      return;
    }

    const categoryTramites = filteredTramites.filter(
      (t) => t.category_id === targetTramite.category_id,
    );
    const draggedIndex = categoryTramites.findIndex(
      (t) => t._id === draggedTramite._id,
    );
    const targetIndex = categoryTramites.findIndex(
      (t) => t._id === targetTramite._id,
    );

    const newOrder = [...categoryTramites];
    const [removed] = newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, removed);

    // Actualizar estado local
    const updatedTramites = tramites.map((t) => {
      const newOrderItem = newOrder.find((nt) => nt._id === t._id);
      if (newOrderItem) {
        const newIndex = newOrder.findIndex((nt) => nt._id === t._id);
        return { ...t, display_order: newIndex };
      }
      return t;
    });

    setTramites(updatedTramites);
    handleReorderTramitesAPI(targetTramite.category_id, newOrder);
  };

  const handleReorderTramitesAPI = async (categoryId, newOrder) => {
    try {
      const token = localStorage.getItem("token");
      const orderData = newOrder.map((tramite, index) => ({
        id: tramite._id,
        order: index,
      }));

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/admin/categories/${categoryId}/applications/reorder`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(orderData),
        },
      );

      if (response.ok) {
        const result = await response.json();
        success(<SquareCheckBig /> + `${result.message}`);

        await fetchData();
      } else {
        const error = await response.json();
        console.error("❌ Error reordenando:", error);
        success(`❌ Error: ${error.detail}`);
        fetchData();
      }
    } catch (error) {
      console.error("❌ Error API reordenamiento:", error);
      success("❌ Error de conexión al reordenar");
      fetchData();
    }
  };

  const openTramiteModal = (tramite) => {
    setEditingTramite(tramite);
    setShowTramiteModal(true);
  };

  if (loading)
    return (
      <div className="p-8">
        <LoadingSpinner />
      </div>
    );

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          Gestión de Trámites
        </h2>
        <div className="flex space-x-3">
          <button
            onClick={handleAutoConnectForms}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors font-medium"
          >
            Auto-Conectar Formularios
          </button>
          <button
            onClick={handleCreateMissingForms}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors font-medium"
          >
            Crear Formularios Faltantes
          </button>
          <button
            onClick={() => setShowTramiteModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex row items-center"
          >
            <Plus className="mr-1" /> Nuevo Trámite
          </button>
        </div>
      </div>

      {/* Category Filter con instrucciones */}
      <div className="mb-6 space-y-3">
        <div className="flex items-center justify-between">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">Todas las categorías</option>
            {categories.map((cat) => (
              <option key={cat._id} value={cat._id}>
                {cat.name}
              </option>
            ))}
          </select>
          <select
            value={selectedStates}
            onChange={(e) => setSelectedStates(e.target.value)}
            className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Todos los Estados</option>
            {states.map((state) => (
              <option key={state._id} value={state._id}>
                {state.name}
              </option>
            ))}
          </select>

          <div className="text-sm text-gray-600 space-x-4">
            {selectedCategory !== "all" ? (
              <span className="text-blue-600 font-medium flex row">
                <Target className="mr-1" /> Arrastra para reordenar dentro de esta categoría
              </span>
            ) : (
              <span className="text-gray-500">
                Selecciona una categoría para reordenar trámites
              </span>
            )}
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="text-sm">
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-blue-600 font-bold flex row items-center gap-1">
                <Lightbulb />
                Cómo reordenar trámites:
              </span>
            </div>
            <div className="text-blue-800 space-y-1">
              <div>
                • <strong>Paso 1:</strong> Selecciona una categoría específica
                (no "Todos")
              </div>
              <div>
                • <strong>Paso 2:</strong> Arrastra el círculo morado con el
                número para reordenar
              </div>
              <div>
                • <strong>Paso 3:</strong> El orden se guarda automáticamente
              </div>
              <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded flex row items-center">
                <TriangleAlert className="mr-1" /> <strong>Importante: </strong>{" "}
                para reordenar, debes filtrar por categoría específica primero
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-4">
        {filteredTramites.map((tramite, index) => {
          const category = categories.find(
            (c) => c._id === tramite.category_id,
          );
          const canDrag = selectedCategory !== "all";

          return (
            <TramiteCardAdvanced
              key={tramite._id}
              tramite={tramite}
              category={category}
              index={index}
              canDrag={canDrag}
              categories={categories}
              onEdit={() => openTramiteModal(tramite)}
              onDelete={() => handleDeleteTramite(tramite._id)}
              onConfigureForm={() => {
                // Cambiar al tab de formularios para este trámite
                if (onConfigureForm) {
                  onConfigureForm(tramite._id);
                }
                success(
                  `Para configurar el formulario del trámite "${tramite.name}", revisa el tab "Editor Formularios". Si no existe un formulario para este trámite, puedes crear uno nuevo.`,
                );
              }}
              onChangeCategory={() => {
                setSelectedTramiteForMove(tramite);
                setShowCategoryChangeModal(true);
              }}
              onConnectForm={() => {
                setTramiteParaConectar(tramite);
                setShowFormConnectionModal(true);
              }}
              onDragStart={(e) => handleTramiteDragStart(e, tramite)}
              onDragEnd={handleTramiteDragEnd}
              onDragOver={handleTramiteDragOver}
              onDrop={(e) => handleTramiteDrop(e, tramite)}
              isDragged={draggedTramite?._id === tramite._id}
            />
          );
        })}
      </div>

      {/* Modal para cambiar categoría */}
      {showCategoryChangeModal && selectedTramiteForMove && (
        <CategoryChangeModal
          tramite={selectedTramiteForMove}
          categories={categories}
          onConfirm={handleChangeTramiteCategory}
          onClose={() => {
            setShowCategoryChangeModal(false);
            setSelectedTramiteForMove(null);
          }}
        />
      )}

      {showTramiteModal && (
        <TramiteModal
          tramite={editingTramite}
          categories={categories}
          states={states}
          onSave={
            editingTramite !== null ? handleUpdateTramite : handleCreateTramite
          }
          onClose={() => {
            setEditingTramite(null);
            setShowTramiteModal(false);
          }}
          editting={editingTramite}
        />
      )}

      {/* Modal para conectar formulario manualmente */}
      {showFormConnectionModal && tramiteParaConectar && (
        <FormConnectionModal
          tramite={tramiteParaConectar}
          formularios={formularios}
          onConnect={handleConnectFormManual}
          onClose={() => {
            setShowFormConnectionModal(false);
            setTramiteParaConectar(null);
          }}
        />
      )}
    </div>
  );
};

const DocumentsTab = () => {
  const [pendingDocs, setPendingDocs] = useState([]);
  const { success, error } = useNotifications();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPendingDocuments();
  }, []);

  const fetchPendingDocuments = async () => {
    try {
      const data = await adminAPI.getPendingDocuments();
      setPendingDocs(data);
    } catch (error) {
      console.error("Error fetching pending documents:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleValidateDocument = async (docId, status, notes = "") => {
    try {
      await adminAPI.validateDocument(docId, { status, notes });
      fetchPendingDocuments();
      success("Documento validado exitosamente");
    } catch (error) {
      success("Error validando documento: " + error.message);
    }
  };

  if (loading)
    return (
      <div className="p-8">
        <LoadingSpinner />
      </div>
    );

  return (
    <div className="p-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">
        Documentos Pendientes de Revisión ({pendingDocs.length})
      </h2>

      <div className="space-y-4">
        {pendingDocs.map((doc) => (
          <DocumentCard
            key={doc._id}
            document={doc}
            onApprove={() => handleValidateDocument(doc._id, "approved")}
            onReject={(notes) =>
              handleValidateDocument(doc._id, "rejected", notes)
            }
          />
        ))}

        {pendingDocs.length === 0 && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📄</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No hay documentos pendientes
            </h3>
            <p className="text-gray-500">
              Todos los documentos han sido revisados
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const TramiteCardAdvanced = ({
  tramite,
  category,
  index,
  canDrag,
  categories,
  onEdit,
  onDelete,
  onConfigureForm,
  onChangeCategory,
  onConnectForm,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  isDragged,
}) => {
  const [isDraggedOver, setIsDraggedOver] = useState(false);

  const handleDragEnter = (e) => {
    if (!canDrag) return;
    e.preventDefault();
    setIsDraggedOver(true);
  };

  const handleDragLeave = (e) => {
    if (!canDrag) return;
    e.preventDefault();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDraggedOver(false);
    }
  };

  const handleDropWrapper = (e) => {
    if (!canDrag) return;
    e.preventDefault();
    setIsDraggedOver(false);
    onDrop(e);
  };

  return (
    <div
      draggable={canDrag}
      onDragStart={canDrag ? onDragStart : undefined}
      onDragEnd={canDrag ? onDragEnd : undefined}
      onDragOver={canDrag ? onDragOver : undefined}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDropWrapper}
      className={`bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all duration-300 ${
        canDrag ? "cursor-move" : "cursor-default"
      } ${
        isDragged
          ? "opacity-50 scale-95 border-purple-400"
          : isDraggedOver
            ? "border-purple-500 bg-purple-50 scale-105 shadow-xl opacity-100"
            : "hover:border-gray-300 opacity-100"
      }`}
      style={
        canDrag
          ? {
              touchAction: "none",
              userSelect: "none",
              WebkitUserSelect: "none",
            }
          : {}
      }
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4 flex-1">
          {/* Handle de drag para trámites */}
          {canDrag && (
            <div className="flex flex-col items-center cursor-grab active:cursor-grabbing">
              <div className="bg-purple-100 text-purple-600 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold mb-1 hover:bg-purple-200 transition-colors">
                {index + 1}
              </div>
              <div className="text-sm text-purple-400 leading-none hover:text-purple-600 transition-colors">
                ⋮⋮
              </div>
              <div className="text-xs text-purple-500 font-medium">drag</div>
            </div>
          )}

          {!canDrag && (
            <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-xs font-bold text-gray-500">
              #
            </div>
          )}

          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              <h3 className="font-semibold text-gray-900">{tramite.name}</h3>
              <span
                className={`px-2 py-1 rounded text-xs font-medium ${
                  tramite.price_type === "fijo"
                    ? "bg-green-100 text-green-800"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                {tramite.price_type === "fijo"
                  ? `$${tramite.fixed_price}`
                  : "Variable"}
              </span>
            </div>
            <p className="text-sm text-gray-500 mb-2">{tramite.description}</p>
            <div className="flex items-center space-x-4">
              <span className="text-xs text-gray-500 flex row items-center gap-2">
                <Folder /> {category?.name || "Sin categoría"}
              </span>
              <span className="text-xs text-gray-500 flex row items-center gap-2">
                <Clock className="mr-1" /> {tramite.estimated_time_days} días
              </span>
              <span className="text-xs text-gray-500 flex row items-center gap-2">
                <File /> {tramite.required_documents?.length || 0} docs
              </span>
            </div>

            {/* Información del formulario con ID único */}
            <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700 flex row items-center">
                    <Clipboard /> Formulario Auto:
                  </span>
                  <span className="text-sm text-blue-700 font-semibold">
                    {tramite.form_template_name || tramite.name}
                  </span>
                </div>
                <div className="flex space-x-2">
                  {tramite.form_template_id ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const formId = tramite.form_template_id.substring(0, 7);
                        window.location.href = `/admin?tab=forms&search=${formId}`;
                      }}
                      className="text-xs text-green-600 hover:text-green-800 font-medium hover:bg-green-50 px-2 py-1 rounded flex row items-center gap-2"
                      title="Buscar este formulario en el Editor"
                    >
                      <Search />
                      Buscar Formulario
                    </button>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onConnectForm();
                      }}
                      className="text-xs text-orange-600 hover:text-orange-800 font-medium hover:bg-orange-50 px-2 py-1 rounded flex row items-center gap-2"
                      title="Conectar formulario manualmente"
                    >
                      <Link /> Conectar Formulario
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onConfigureForm();
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium hover:bg-blue-50 px-2 py-1 rounded flex row items-center gap-2"
                  >
                    <Edit /> Editar Formulario
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between mt-2">
                <div className="text-xs text-gray-500 flex row items-center gap-2">
                  Estado: <SquareCheckBig />{" "}
                  {tramite.has_custom_form ? "Personalizado" : "Auto-generado"}
                </div>
                <div className="text-xs font-mono bg-white px-2 py-1 rounded border flex row items-center gap-2">
                  {tramite.form_template_id ? (
                    <>
                      <IdCard /> Form ID:{" "}
                      <span className="text-green-600 font-bold">
                        {tramite.form_template_id.substring(0, 7)}
                      </span>
                    </>
                  ) : (
                    <span className="text-red-600 font-bold flex row items-center gap-2">
                      <X /> Sin Formulario Conectado
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex space-x-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onChangeCategory();
            }}
            className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors flex row items-center gap-2"
            title="Cambiar Categoría"
          >
            <RefreshCcw />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onConfigureForm();
            }}
            className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors flex row items-center gap-2"
            title="Configurar Formulario"
          >
            <Settings />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onEdit();
            }}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex row items-center gap-2"
            title="Editar"
          >
            <Edit />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onDelete();
            }}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors flex row items-center gap-2"
            title="Eliminar"
          >
            <Trash />
          </button>
        </div>
      </div>

      {/* Indicador visual para drag */}
      {canDrag && isDraggedOver && (
        <div className="absolute inset-0 border-4 border-dashed border-purple-500 rounded-lg bg-purple-100 bg-opacity-50 flex items-center justify-center pointer-events-none">
          <div className="text-lg font-bold text-purple-600">
            📍 Soltar para reordenar
          </div>
        </div>
      )}

      {canDrag && (
        <div className="mt-2 text-xs text-gray-400 text-center">
          {isDraggedOver
            ? "📍 Soltar aquí"
            : "<Target /> Arrastra para reordenar"}
        </div>
      )}
    </div>
  );
};

const DocumentCard = ({ document, onApprove, onReject }) => {
  const [rejectNotes, setRejectNotes] = useState("");
  const [showRejectForm, setShowRejectForm] = useState(false);

  const handleReject = () => {
    if (rejectNotes.trim()) {
      onReject(rejectNotes);
      setShowRejectForm(false);
      setRejectNotes("");
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-4">
          <div className="text-2xl">📄</div>
          <div>
            <h3 className="font-semibold text-gray-900">
              {document.document_name}
            </h3>
            <p className="text-sm text-gray-500">
              Cliente: {document.client_name} | Trámite: {document.tramite_name}
            </p>
            <p className="text-xs text-gray-400">
              Subido el {new Date(document.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={onApprove}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex row items-center"
          >
            <SquareCheckBig className="mr-1" /> Aprobar
          </button>
          <button
            onClick={() => setShowRejectForm(true)}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex row items-center"
          >
            <X className="mr-1" /> Rechazar
          </button>
        </div>
      </div>

      {showRejectForm && (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
          <textarea
            value={rejectNotes}
            onChange={(e) => setRejectNotes(e.target.value)}
            placeholder="Motivo del rechazo..."
            className="w-full h-20 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
          />
          <div className="flex justify-end space-x-2 mt-3">
            <button
              onClick={() => setShowRejectForm(false)}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleReject}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Confirmar Rechazo
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const UsersTab = () => (
  <div className="p-8">
    <h2 className="text-2xl font-bold text-gray-900 mb-6">
      Gestión de Usuarios
    </h2>
    <div className="bg-gray-50 rounded-lg p-8 text-center">
      <UserAdminPanel />
    </div>
  </div>
);

const SupportTab = () => (
  <div className="p-8">
    <h2 className="text-2xl font-bold text-gray-900 mb-6">
      Gestión de Usuarios
    </h2>
    <div className="bg-gray-50 rounded-lg p-8 text-center">
      <ChatAdminPanel />
    </div>
  </div>
);

const GestoresTab = () => {
  const [gestores, setGestores] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGestoresData();
  }, []);

  const fetchGestoresData = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/admin/workload`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.ok) {
        const data = await response.json();
        setGestores(data.gestores || []);
      }
    } catch (error) {
      console.error("Error fetching gestores:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading)
    return (
      <div className="p-8">
        <LoadingSpinner />
      </div>
    );

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Gestión de Gestores
          </h2>
          <p className="text-gray-600">Carga de trabajo y especialización</p>
        </div>
      </div>

      {/* Lista de gestores */}
      <div className="grid gap-4">
        {gestores.map((gestor) => (
          <div
            key={gestor.gestor_id}
            className="bg-white border border-gray-200 rounded-lg p-6"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="text-3xl">
                  <UserRoundCog />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {gestor.name}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {gestor.current_assignments} asignaciones de{" "}
                    {gestor.max_concurrent} máx
                  </p>
                  <p className="text-sm text-gray-500">
                    Carga: {gestor.workload_percentage.toFixed(1)}%
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminPanel;
